import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

// THUNKS
// Ottiene la lista di tutti gli equipment
export const getEquipmentsList = createAsyncThunk(
    'equipment/getEquipmentsList',
    async ({ accountId, token, search, start, length }, { rejectWithValue }) => {
    try {
        let url = `${import.meta.env.VITE_API_BASE_URL}/equipments/list?accountId=${accountId}&start=${start}&length=${length}&draw=1`;
        
        // Aggiungo i parametri di ricerca se presenti
        if (search) {
            url += `&search[value]=${encodeURIComponent(search)}`;
        }
        
        const headers = {
            'Content-Type': 'application/json',
            'auth-token': token
        };
        const options = { method: 'GET', headers };
        
        const response = await fetch(url, options);
        
        if (!response.ok) {
            const error = await response.json();
            return rejectWithValue({
                message: error.message || `Errore sconosciuto nel caricamento degli equipment.`
            });
        }
        const result = await response.json();
        return result;
    } catch (error) {
        return rejectWithValue({
            message: (error instanceof Error) ? error.message : 'Errore di rete. Riprova più tardi.'
        });
    }
});

// Ottiene i dettagli di un equipment specifico
export const getEquipmentDetails = createAsyncThunk(
    'equipment/getEquipmentDetails',
    async ({ equipmentId, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/equipments?equipmentId=${equipmentId}`;
        const headers = {
            'Content-Type': 'application/json',
            'auth-token': token
        };
        const options = { method: 'GET', headers };
        
        const response = await fetch(url, options);
        
        if (!response.ok) {
            const error = await response.json();
            return rejectWithValue({
                message: error.message || `Errore sconosciuto nel caricamento dei dettagli dell'equipment.`
            });
        }
        const result = await response.json();
        return result;
    } catch (error) {
        return rejectWithValue({
            message: (error instanceof Error) ? error.message : 'Errore di rete. Riprova più tardi.'
        });
    }
});

// Crea un nuovo equipment
export const createEquipment = createAsyncThunk(
    'equipment/createEquipment',
    async ({ equipment, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/equipments`;
        const headers = {
            'Content-Type': 'application/json',
            'auth-token': token
        };
        const options = { 
            method: 'POST', 
            headers, 
            body: JSON.stringify({ equipment })
        };
        
        const response = await fetch(url, options);
        
        if (!response.ok) {
            const error = await response.json();
            return rejectWithValue({
                message: error.message || `Errore sconosciuto nella creazione dell'equipment.`
            });
        }
        const result = await response.json();
        return result;
    } catch (error) {
        return rejectWithValue({
            message: (error instanceof Error) ? error.message : 'Errore di rete. Riprova più tardi.'
        });
    }
});

// Aggiorna un equipment esistente
export const updateEquipment = createAsyncThunk(
    'equipment/updateEquipment',
    async ({ equipment, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/equipments`;
        const headers = {
            'Content-Type': 'application/json',
            'auth-token': token
        };
        const options = { 
            method: 'PATCH', 
            headers, 
            body: JSON.stringify({ equipment })
        };
        
        const response = await fetch(url, options);
        
        if (!response.ok) {
            const error = await response.json();
            return rejectWithValue({
                message: error.message || `Errore sconosciuto nell'aggiornamento dell'equipment.`
            });
        }
        const result = await response.json();
        return result;
    } catch (error) {
        return rejectWithValue({
            message: (error instanceof Error) ? error.message : 'Errore di rete. Riprova più tardi.'
        });
    }
});

// Elimina un equipment
export const deleteEquipment = createAsyncThunk(
    'equipment/deleteEquipment',
    async ({ equipmentId, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/equipments`;
        const headers = {
            'Content-Type': 'application/json',
            'auth-token': token
        };
        const options = { 
            method: 'DELETE', 
            headers, 
            body: JSON.stringify({ equipmentId })
        };
        
        const response = await fetch(url, options);
        
        if (!response.ok) {
            const error = await response.json();
            return rejectWithValue({
                message: error.message || `Errore sconosciuto nell'eliminazione dell'equipment.`
            });
        }
        const result = await response.json();
        return result;
    } catch (error) {
        return rejectWithValue({
            message: (error instanceof Error) ? error.message : 'Errore di rete. Riprova più tardi.'
        });
    }
});

const initialState = {
    data: null,
    equipmentDetails: null,
    error: null,
    isLoading: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false
};

const equipmentSlice = createSlice({
    name: 'equipment',
    initialState,
    reducers: {
        clearEquipmentData: (state) => {
            state.data = null;
            state.equipmentDetails = null;
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // getEquipmentsList
            .addCase(getEquipmentsList.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getEquipmentsList.fulfilled, (state, action) => {
                state.data = action.payload;
                state.error = null;
                state.isLoading = false;
            })
            .addCase(getEquipmentsList.rejected, (state, action) => {
                state.error = action.payload?.message || "Errore sconosciuto nel caricamento degli equipment.";
                state.isLoading = false;
            })
            // getEquipmentDetails
            .addCase(getEquipmentDetails.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getEquipmentDetails.fulfilled, (state, action) => {
                state.equipmentDetails = action.payload;
                state.error = null;
                state.isLoading = false;
            })
            .addCase(getEquipmentDetails.rejected, (state, action) => {
                state.error = action.payload?.message || "Errore sconosciuto nel caricamento dei dettagli dell'equipment.";
                state.isLoading = false;
            })
            // createEquipment
            .addCase(createEquipment.pending, (state) => {
                state.isCreating = true;
                state.error = null;
            })
            .addCase(createEquipment.fulfilled, (state, action) => {
                state.equipmentDetails = action.payload;
                state.error = null;
                state.isCreating = false;
            })
            .addCase(createEquipment.rejected, (state, action) => {
                state.error = action.payload?.message || "Errore sconosciuto nella creazione dell'equipment.";
                state.isCreating = false;
            })
            // updateEquipment
            .addCase(updateEquipment.pending, (state) => {
                state.isUpdating = true;
                state.error = null;
            })
            .addCase(updateEquipment.fulfilled, (state, action) => {
                state.equipmentDetails = action.payload;
                state.error = null;
                state.isUpdating = false;
            })
            .addCase(updateEquipment.rejected, (state, action) => {
                state.error = action.payload?.message || "Errore sconosciuto nell'aggiornamento dell'equipment.";
                state.isUpdating = false;
            })
            // deleteEquipment
            .addCase(deleteEquipment.pending, (state) => {
                state.isDeleting = true;
                state.error = null;
            })
            .addCase(deleteEquipment.fulfilled, (state) => {
                state.equipmentDetails = null;
                state.error = null;
                state.isDeleting = false;
            })
            .addCase(deleteEquipment.rejected, (state, action) => {
                state.error = action.payload?.message || "Errore sconosciuto nell'eliminazione dell'equipment.";
                state.isDeleting = false;
            })
    }
});

// Selectors
export const selectEquipmentsData = (state) => state.equipment.data;
export const selectEquipmentDetails = (state) => state.equipment.equipmentDetails;
export const selectEquipmentLoading = (state) => state.equipment.isLoading;
export const selectEquipmentCreating = (state) => state.equipment.isCreating;
export const selectEquipmentUpdating = (state) => state.equipment.isUpdating;
export const selectEquipmentDeleting = (state) => state.equipment.isDeleting;
export const selectEquipmentError = (state) => state.equipment.error;

export const { clearEquipmentData } = equipmentSlice.actions;
export default equipmentSlice.reducer;
